import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { apiService, ApiError } from '../../services/api';
import { TradeLineAdviceOption, TradeLineAdviceResponse } from '../../types/api';
import { formatCurrency } from '../../lib/utils';
import { Loader2, Calendar, CreditCard, Banknote, Info, Sparkles } from 'lucide-react';

interface AddTradelineDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOrderCreated: () => void;
}

export function AddTradelineDialog({
  isOpen,
  onOpenChange,
  onOrderCreated,
}: AddTradelineDialogProps) {
  const [adviceRequest, setAdviceRequest] = useState({
    desiredAverageAgeIncrease: 1,
    desiredExtraLimit: 25000,
  });
  const [currentAverageAge, setCurrentAverageAge] = useState<number>(0);
  const [currentBureauCreditLimits, setCurrentBureauCreditLimits] = useState<Record<string, number>>({
    Equifax: 0,
    Experian: 0,
    TransUnion: 0,
  });
  const [adviceOptions, setAdviceOptions] = useState<TradeLineAdviceOption[]>([]);
  const [isLoadingAdviceOptions, setIsLoadingAdviceOptions] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [showAdvancedCalculator, setShowAdvancedCalculator] = useState(false);
  const [isLoadingCurrentAge, setIsLoadingCurrentAge] = useState(false);
  const [adviceResponse, setAdviceResponse] = useState<TradeLineAdviceResponse | null>(null);
  const [isLoadingAdvice, setIsLoadingAdvice] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAmexTradeline = (provider?: string, name?: string): boolean => {
    const normalized = `${provider || ''} ${name || ''}`.toLowerCase();
    return normalized.includes('amex') || normalized.includes('american express');
  };

  const parseCreditAge = (rawValue: unknown): number | null => {
    if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) return rawValue;
    if (typeof rawValue !== 'string') return null;
    const match = rawValue.match(/(\d+(\.\d+)?)/);
    if (!match) return null;
    const parsed = parseFloat(match[1]);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const fetchCurrentAverageAge = async () => {
    try {
      setIsLoadingCurrentAge(true);
      const storedUser = sessionStorage.getItem('user');
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;
      const bureauCount = (parsedUser?.maxPlanEnabled || parsedUser?.revenueCatId) ? 3 : 1;
      const analytics = await apiService.getCreditAnalytics(bureauCount);
      const creditFactors = Array.isArray(analytics?.creditFactors)
        ? analytics.creditFactors
        : (analytics?.creditFactors as { factors?: Array<{ title?: string; value?: unknown }> })?.factors || [];
      const bureauCreditLimits = analytics?.fullReport?.summary?.bureauCreditLimits || {};
      setCurrentBureauCreditLimits({
        Equifax: bureauCreditLimits.Equifax || 0,
        Experian: bureauCreditLimits.Experian || 0,
        TransUnion: bureauCreditLimits.TransUnion || 0,
      });

      const creditAgeFactor = creditFactors.find(
        (factor) => String(factor?.title || '').toLowerCase() === 'credit age'
      );
      const parsedAge = parseCreditAge(creditAgeFactor?.value);
      if (parsedAge !== null) {
        setCurrentAverageAge(parsedAge);
      } else {
        setCurrentAverageAge(0);
      }
    } catch {
      setCurrentAverageAge(0);
      setCurrentBureauCreditLimits({
        Equifax: 0,
        Experian: 0,
        TransUnion: 0,
      });
    } finally {
      setIsLoadingCurrentAge(false);
    }
  };

  const fetchAdviceOptions = async () => {
    try {
      setIsLoadingAdviceOptions(true);
      const options = await apiService.getTradeLineAdviceOptions();
      const levelOrder: Record<string, number> = { basic: 0, medium: 1, high: 2 };
      const sortedOptions = [...options].sort(
        (a, b) => (levelOrder[a.increaseLevel] ?? 99) - (levelOrder[b.increaseLevel] ?? 99)
      );
      setAdviceOptions(sortedOptions);
      if (sortedOptions.length > 0) {
        setSelectedOptionIndex(0);
        setAdviceResponse(sortedOptions[0].advice);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to load recommended offers';
      setError(`${errorMessage}. You can still use Advanced calculator.`);
    } finally {
      setIsLoadingAdviceOptions(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    fetchCurrentAverageAge();
    fetchAdviceOptions();
  }, [isOpen]);

  const getTradelineAdvice = async () => {
    try {
      setIsLoadingAdvice(true);
      setError('');
      setSuccess('');

      const response = await apiService.getTradeLineAdvice(
        {
          desiredAverageAge: currentAverageAge + adviceRequest.desiredAverageAgeIncrease,
          desiredAverageAgeIncrease: adviceRequest.desiredAverageAgeIncrease,
          currentAverageAge,
          desiredExtraLimit: adviceRequest.desiredExtraLimit,
        },
        false
      );

      setSelectedOptionIndex(null);
      setAdviceResponse(response);
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to get tradeline advice';
      setError(errorMessage);
    } finally {
      setIsLoadingAdvice(false);
    }
  };

  const handleCheckout = async () => {
    if (!adviceResponse?.selectedTradeLines?.length) {
      setError('No recommended tradelines to checkout');
      return;
    }

    const storedUser = sessionStorage.getItem('user');
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    const userId = parsedUser?.id;

    if (!userId) {
      setError('User ID not found. Please refresh and try again.');
      return;
    }

    try {
      setIsCreatingOrder(true);
      setError('');
      setSuccess('');

      const inventoryIds = adviceResponse.selectedTradeLines
        .map((selection) => selection.tradeLine.inventoryId || (selection.tradeLine as unknown as { id?: string }).id)
        .filter((inventoryId): inventoryId is string => Boolean(inventoryId));

      if (inventoryIds.length !== adviceResponse.selectedTradeLines.length) {
        setError('Missing inventory id in recommended tradelines. Please refresh recommendations.');
        return;
      }

      const response = await apiService.createTradelineOrder({
        user_id: userId,
        items: inventoryIds.map((tradelineInventoryId) => ({
          tradeline_inventory_id: tradelineInventoryId,
          quantity: 1,
        })),
      });

      setSuccess('Order created. Redirecting to checkout...');
      onOrderCreated();

      if (response?.data?.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
      }

      setTimeout(() => {
        handleClose();
      }, 800);
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to create tradeline order';
      setError(errorMessage);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after closing
    setTimeout(() => {
      setAdviceResponse(null);
      setAdviceOptions([]);
      setSelectedOptionIndex(null);
      setShowAdvancedCalculator(false);
      setError('');
      setSuccess('');
    }, 200);
  };

  const handleSelectAdviceOption = (option: TradeLineAdviceOption, index: number) => {
    setSelectedOptionIndex(index);
    setAdviceResponse(option.advice);
    setError('');
  };

  const getOfferLevelLabel = (level: string): string => {
    const normalized = (level || '').toLowerCase();
    if (normalized === 'basic') return 'Basic';
    if (normalized === 'medium') return 'Medium';
    if (normalized === 'high') return 'High';
    return level || 'Offer';
  };

  const selectedAdviceOption = selectedOptionIndex !== null ? adviceOptions[selectedOptionIndex] : null;
  const canCheckout = Boolean(adviceResponse?.selectedTradeLines?.length);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
        style={{ width: '900px' }}
      >
        <DialogHeader className="flex-shrink-0 w-full space-y-0" style={{ paddingLeft: '24px', paddingRight: '72px', paddingTop: '24px', paddingBottom: '24px' }}>
          <DialogTitle className="text-lg font-semibold">Add Tradeline</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-6 w-full" style={{ paddingLeft: '24px', paddingRight: '24px', paddingBottom: '24px' }}>
          {/* Baseline Reference */}
          <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50/50" style={{ borderRadius: '8px' }}>
            <div>
              <h3 className="text-base font-semibold mb-1">Current Baseline</h3>
              <p className="text-sm text-gray-500">
                Reference values used to evaluate recommendation targets.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
                <p className="text-xs text-gray-500">Current Average Age</p>
                <p className="text-sm font-semibold text-gray-900">
                  {isLoadingCurrentAge ? 'Loading...' : `${currentAverageAge.toFixed(1)} years`}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {(['Equifax', 'Experian', 'TransUnion'] as const).map((bureau) => (
                <div key={bureau} className="rounded-md border border-gray-200 bg-white px-3 py-2">
                  <p className="text-xs text-gray-500">{bureau}</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {isLoadingCurrentAge
                      ? 'Loading...'
                      : formatCurrency(currentBureauCreditLimits[bureau] || 0)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Offers */}
          <div className="space-y-4 border border-gray-200 rounded-lg p-4" style={{ borderRadius: '8px' }}>
            <div>
              <h3 className="text-base font-semibold mb-1">Recommended Offers</h3>
              <p className="text-sm text-gray-500">
                Precomputed offers based on current profile baseline (+age and +credit limit):
              </p>
            </div>

            {isLoadingAdviceOptions ? (
              <div className="flex items-center gap-2 text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-sm">Loading recommended offers...</p>
              </div>
            ) : adviceOptions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {adviceOptions.map((option, index) => {
                  const isSelected = selectedOptionIndex === index;
                  return (
                    <div
                      key={`${option.increaseLevel}-${index}`}
                      className={`rounded-lg border p-4 ${isSelected ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800">
                          <Sparkles className="h-3.5 w-3.5" />
                          {getOfferLevelLabel(option.increaseLevel)}
                        </div>
                      </div>
                      <div className="space-y-1 text-sm text-gray-700 mb-3">
                        <p>Projected age: {(option.advice?.newAverageAge ?? 0).toFixed(1)} years</p>
                        <p>New limit: {formatCurrency(option.advice?.newTotalLimit || 0)}</p>
                        <p>Tradelines: {option.advice?.selectedTradeLines?.length || 0}</p>
                        <p className="font-semibold text-gray-900">
                          Total cost: {formatCurrency(option.advice?.totalCost || 0)}
                        </p>
                      </div>
                      <Button
                        variant={isSelected ? 'default' : 'outline'}
                        className={isSelected ? 'bg-black hover:bg-gray-800 text-white' : ''}
                        onClick={() => handleSelectAdviceOption(option, index)}
                      >
                        {isSelected ? 'Selected' : 'Use this offer'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                No precomputed offers available right now.
              </div>
            )}

            <div className="pt-1">
              <Button
                variant="ghost"
                className="h-auto px-0 text-sm text-gray-600 hover:text-gray-900"
                onClick={() => setShowAdvancedCalculator((prev) => !prev)}
              >
                {showAdvancedCalculator ? 'Hide advanced calculator' : 'Use advanced calculator'}
              </Button>
            </div>
          </div>

          {/* Advanced Calculator Section */}
          {showAdvancedCalculator && (
            <div className="space-y-4 border border-gray-200 rounded-lg p-4" style={{ borderRadius: '8px' }}>
              <div>
                <h3 className="text-base font-semibold mb-1">Advanced Calculator</h3>
                <p className="text-sm text-gray-500">
                  Manually adjust age increase and credit limit targets for a custom recommendation:
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Current Average Age</label>
                <div className="h-9 w-full rounded-md border border-gray-200 bg-gray-50 px-3 flex items-center text-sm font-medium text-gray-900">
                  {isLoadingCurrentAge ? 'Loading...' : `${currentAverageAge.toFixed(1)} years`}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Desired Average Age Increase</label>
                <Input
                  type="number"
                  step="0.1"
                  value={adviceRequest.desiredAverageAgeIncrease}
                  onChange={(e) => setAdviceRequest({
                    ...adviceRequest,
                    desiredAverageAgeIncrease: parseFloat(e.target.value) || 0
                  })}
                  className="w-full h-9"
                  style={{ height: '36px' }}
                  placeholder="Years: 1"
                />
                <div className="flex gap-2">
                  {[1, 3, 5, 10].map((years) => (
                    <Button
                      key={years}
                      variant="outline"
                      size="sm"
                      className="px-3 py-0 text-xs bg-gray-100 hover:bg-gray-200 border-0"
                      style={{ borderRadius: '16px', height: '24px' }}
                      onClick={() => setAdviceRequest({
                        ...adviceRequest,
                        desiredAverageAgeIncrease: years
                      })}
                    >
                      {years}y
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Current Credit Limits by Bureau</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['Equifax', 'Experian', 'TransUnion'] as const).map((bureau) => (
                    <div key={bureau} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">{bureau}</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {isLoadingCurrentAge
                          ? 'Loading...'
                          : formatCurrency(currentBureauCreditLimits[bureau] || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Desired Extra Credit Limit</label>
                <div className="relative w-full">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    value={adviceRequest.desiredExtraLimit}
                    onChange={(e) => setAdviceRequest({
                      ...adviceRequest,
                      desiredExtraLimit: parseInt(e.target.value) || 0
                    })}
                    className="pl-7 h-9 w-full"
                    style={{ height: '36px' }}
                    placeholder="25,000"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000].map((amount) => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      className="px-3 py-0 text-xs bg-gray-100 hover:bg-gray-200 border-0"
                      style={{ borderRadius: '16px', height: '24px' }}
                      onClick={() => setAdviceRequest({
                        ...adviceRequest,
                        desiredExtraLimit: amount
                      })}
                    >
                      ${amount / 1000}K
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex justify-start pt-2">
                <Button
                  onClick={getTradelineAdvice}
                  disabled={isLoadingAdvice}
                  className="bg-black hover:bg-gray-800 text-white"
                >
                  {isLoadingAdvice ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Get Custom Recommendation
                </Button>
              </div>
            </div>
          )}

          {/* Advice Results */}
          {adviceResponse && (
            <div className="space-y-6">
              {/* Tradeline Recommendation Section */}
              <div className="border border-gray-200 rounded-lg p-4" style={{ borderRadius: '8px' }}>
                <h3 className="text-base font-semibold mb-4">Tradeline Recommendation</h3>
                
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {selectedAdviceOption ? 'Target Average Age' : 'New Average Age'}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {(selectedAdviceOption
                        ? adviceResponse?.newAverageAge
                        : adviceResponse?.newAverageAge
                      )?.toFixed(1) || 0} years
                    </p>
                    {selectedAdviceOption && (
                      <p className="mt-1 text-xs text-gray-500">
                        Projected with selected tradelines: {adviceResponse?.newAverageAge?.toFixed(1) || 0} years
                      </p>
                    )}
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                      <p className="text-sm text-gray-600">New Total Limit</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(adviceResponse?.newTotalLimit || 0)}</p>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote className="h-5 w-5 text-gray-400" />
                      <p className="text-sm text-gray-600">Total Cost</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(adviceResponse?.totalCost || 0)}</p>
                  </div>
                </div>
              </div>

              {/* Selected Tradelines */}
              {adviceResponse.selectedTradeLines && adviceResponse.selectedTradeLines.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <Table>
                    <TableHeader className="[&_tr]:border-b-0">
                      {/* Title Row */}
                      <TableRow className="border-b-0">
                        <TableHead colSpan={6} className="px-4 py-3">
                          <h2 className="text-base font-semibold text-gray-900">Recommended Tradelines</h2>
                        </TableHead>
                      </TableRow>
                      
                      {/* Column Headers */}
                      <TableRow className="!border-b !border-gray-200 border-t-0">
                        <TableHead className="pl-4 pr-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                          Tradeline Name
                        </TableHead>
                        <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                          Age
                        </TableHead>
                        <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                          Credit Limit
                        </TableHead>
                        <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                          Price
                        </TableHead>
                        <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                          Slots
                        </TableHead>
                        <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                          Cost
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adviceResponse.selectedTradeLines.map((selection, index) => (
                        <TableRow key={index} className="border-b border-gray-200">
                          <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">
                            {selection.tradeLine.name}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-gray-700">
                            {isAmexTradeline(selection.tradeLine.provider, selection.tradeLine.name)
                              ? '-'
                              : `${selection.tradeLine.ageYears} years`}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-gray-700">
                            {formatCurrency(selection.tradeLine.creditLimit)}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-gray-700">
                            {formatCurrency(selection.tradeLine.price)}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-gray-700">
                            {selection.slots}
                          </TableCell>
                          <TableCell className="px-2 py-3 font-semibold text-gray-900">
                            {formatCurrency(selection.tradeLine.price * selection.slots)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Message */}
              {adviceResponse.message && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <Info className="h-5 w-5 text-gray-700 flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-900">{adviceResponse.message}</p>
                </div>
              )}
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}
        </div>

        {/* Footer with Checkout Button */}
        {adviceResponse && (
          <div className="flex justify-end gap-3 border-t border-gray-200 flex-shrink-0 bg-white w-full" style={{ paddingLeft: '24px', paddingRight: '24px', paddingTop: '24px', paddingBottom: '24px' }}>
            <Button
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={isCreatingOrder || !canCheckout}
              className="bg-black hover:bg-gray-800 text-white"
            >
              {isCreatingOrder ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {canCheckout ? 'Checkout' : 'No Tradelines to Checkout'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
